import { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useEditor, EditorContent, Extension } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import ResizableImage from '../components/ResizableImageExtension';
import Placeholder from '@tiptap/extension-placeholder';
import { TextStyle } from '@tiptap/extension-text-style';
import { Color } from '@tiptap/extension-color';
import { Highlight } from '@tiptap/extension-highlight';
import { Table, TableRow, TableCell, TableHeader } from '@tiptap/extension-table';
import { DOMParser as PMMDOMParser } from '@tiptap/pm/model';
import * as Y from 'yjs';
import { WebsocketProvider } from 'y-websocket';
import { ySyncPlugin, yCursorPlugin, yUndoPlugin } from 'y-prosemirror';
import api from '../utils/api';
import dayjs from 'dayjs';
import {
  ArrowLeft, WifiOff, Calendar, Tag, Users, AlertCircle, Minus, ChevronsUp,
  CheckSquare, Clock, Pencil, Trash2, X,
} from 'lucide-react';
import EditorToolbar from '../components/EditorToolbar';
import MarkdownPaste from '../extensions/MarkdownPaste';
import { markdownToHtml } from '../utils/markdownUtils';
import FileAttachments from '../components/FileAttachments';

const COLUMNS_STATUS = [
  { id: 'todo', label: '예정' },
  { id: 'in_progress', label: '진행중' },
  { id: 'done', label: '완료' },
];

function Avatar({ name = '' }) {
  const initials = name.split(/\s+/).map(w => w[0]).join('').toUpperCase().slice(0, 2) || '?';
  return (
    <span className="w-6 h-6 rounded-full bg-brand/30 text-brand text-[10px] font-bold flex items-center justify-center flex-shrink-0">
      {initials}
    </span>
  );
}

const COLORS = ['#f87171','#fb923c','#facc15','#4ade80','#22d3ee','#818cf8','#e879f9','#f472b6'];
function getUserColor(id) { return COLORS[(id || 0) % COLORS.length]; }

const STATUS_LABEL = { todo: '예정', in_progress: '진행중', done: '완료' };
const STATUS_COLOR = { todo: 'text-gray-400 bg-gray-800', in_progress: 'text-blue-400 bg-blue-900/30', done: 'text-green-400 bg-green-900/30' };
const PRIORITY_MAP = {
  low:    { label: '낮음',  icon: Minus,       color: 'text-gray-400' },
  medium: { label: '보통',  icon: AlertCircle,  color: 'text-yellow-400' },
  high:   { label: '높음',  icon: ChevronsUp,  color: 'text-red-400' },
};

async function uploadImage(file) {
  const fd = new FormData();
  fd.append('image', file);
  const r = await api.post('/boards/upload', fd);
  return r.data.url;
}

export default function KanbanCardPage({ department }) {
  const { cardId } = useParams();
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem('mfg_user') || '{}');

  const [card, setCard] = useState(null);
  const [connected, setConnected] = useState(false);
  const [peers, setPeers] = useState([]);
  const [editModal, setEditModal] = useState(false);
  const [labels, setLabels] = useState([]);
  const [admins, setAdmins] = useState([]);
  const [deleting, setDeleting] = useState(false);
  const [saveStatus, setSaveStatus] = useState(null); // null | 'saving' | 'saved' | 'error'
  const fileInputRef = useRef(null);
  const mdFileInputRef = useRef(null);
  const initDone = useRef(false);
  const saveTimer = useRef(null);
  const saveStatusTimer = useRef(null);

  const { ydoc, provider } = useMemo(() => {
    const doc = new Y.Doc();
    const token = localStorage.getItem('mfg_token');
    const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const isLocal = ['localhost', '127.0.0.1', '100.116.73.97'].includes(window.location.hostname);
    const wsHost = isLocal ? `${window.location.hostname}:3800` : window.location.host;
    const prov = new WebsocketProvider(`${wsProtocol}//${wsHost}/yjs`, `kanban-${cardId}`, doc, { params: { token } });
    return { ydoc: doc, provider: prov };
  }, [cardId]);

  const YjsExtension = useMemo(() => Extension.create({
    name: 'yjs',
    addProseMirrorPlugins() {
      const fragment = ydoc.getXmlFragment('content');
      return [
        ySyncPlugin(fragment),
        yCursorPlugin(provider.awareness, {
          cursorBuilder: (user) => {
            const cursor = document.createElement('span');
            cursor.className = 'yjs-cursor';
            cursor.style.borderColor = user.color;
            const label = document.createElement('span');
            label.className = 'yjs-cursor-label';
            label.style.backgroundColor = user.color;
            label.textContent = user.name;
            cursor.appendChild(label);
            return cursor;
          },
        }),
        yUndoPlugin(),
      ];
    },
  }), [ydoc, provider]);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({ history: false }),
      YjsExtension,
      ResizableImage,
      TextStyle,
      Color,
      Highlight.configure({ multicolor: true }),
      Placeholder.configure({ placeholder: '내용을 입력하세요...' }),
      MarkdownPaste,
      Table.configure({ resizable: false }),
      TableRow,
      TableCell,
      TableHeader,
    ],
    autofocus: false,
    editorProps: {
      attributes: { class: 'prose prose-invert max-w-none focus:outline-none min-h-[400px] px-6 py-5' },
      handleDrop: (view, event) => {
        const files = event.dataTransfer?.files;
        if (files?.length) {
          event.preventDefault();
          Array.from(files).forEach(async (f) => {
            if (!f.type.startsWith('image/')) return;
            const url = await uploadImage(f);
            view.dispatch(view.state.tr.replaceSelectionWith(
              view.state.schema.nodes.image.create({ src: url })
            ));
          });
          return true;
        }
        return false;
      },
      handlePaste: (view, event) => {
        const items = event.clipboardData?.items;
        if (!items) return false;
        for (const item of items) {
          if (item.type.startsWith('image/')) {
            event.preventDefault();
            const file = item.getAsFile();
            if (!file) continue;
            (async () => {
              const url = await uploadImage(file);
              view.dispatch(view.state.tr.replaceSelectionWith(
                view.state.schema.nodes.image.create({ src: url })
              ));
            })();
            return true;
          }
        }
        return false;
      },
    },
  }, [ydoc]);

  const tryInitContent = useCallback(() => {
    if (initDone.current || !editor || !card) return;
    const fragment = ydoc.getXmlFragment('content');
    if (fragment.length === 0 && card.description) {
      initDone.current = true;
      editor.commands.setContent(card.description, false);
    } else if (fragment.length > 0) {
      initDone.current = true;
    }
  }, [editor, card, ydoc]);
  const tryInitRef = useRef(tryInitContent);
  useEffect(() => { tryInitRef.current = tryInitContent; });

  const backPath = `/work/${department}/kanban`;

  useEffect(() => {
    api.get(`/kanban/cards/${cardId}`).then(r => setCard(r.data));
    api.get('/kanban/labels').then(r => setLabels(r.data));
    api.get('/admins/dropdown').then(r => setAdmins(r.data || [])).catch(() => {});
  }, [cardId]);

  const handleDelete = async () => {
    if (!card) return;
    if (!window.confirm(`'${card.title}' 카드를 삭제하시겠습니까?`)) return;
    setDeleting(true);
    await api.delete(`/kanban/cards/${card.id}`);
    navigate(backPath);
  };

  const handleEditSave = async (form) => {
    const res = await api.put(`/kanban/cards/${card.id}`, form);
    const updated = res.data;
    updated.assigneeDetails = (updated.assignees || []).map(id => admins.find(a => a.id === id)).filter(Boolean);
    updated.label = labels.find(l => l.id === updated.labelId) || null;
    setCard(prev => ({ ...prev, ...updated }));
    setEditModal(false);
  };

  useEffect(() => {
    provider.on('status', ({ status }) => setConnected(status === 'connected'));
    if (provider.wsconnected) setConnected(true);

    provider.awareness.setLocalStateField('user', {
      name: user.name || user.username,
      color: getUserColor(user.id),
    });

    const awarenessHandler = () => {
      const seen = new Set();
      const users = [];
      provider.awareness.getStates().forEach(state => {
        if (state.user && !seen.has(state.user.name)) {
          seen.add(state.user.name);
          users.push({ ...state.user, isMe: false });
        }
      });
      const myName = user.name || user.username;
      if (!seen.has(myName)) users.unshift({ name: myName, color: getUserColor(user.id), isMe: true });
      else {
        const idx = users.findIndex(u => u.name === myName);
        if (idx >= 0) { users[idx].isMe = true; if (idx > 0) users.unshift(users.splice(idx, 1)[0]); }
      }
      setPeers(users);
    };
    provider.awareness.on('change', awarenessHandler);
    awarenessHandler();

    const onSync = (isSynced) => { if (isSynced) setTimeout(() => tryInitRef.current(), 300); };
    provider.on('synced', onSync);
    if (provider.synced) setTimeout(() => tryInitRef.current(), 300);

    return () => {
      provider.awareness.off('change', awarenessHandler);
      provider.off('synced', onSync);
      provider.destroy();
      ydoc.destroy();
    };
  }, [provider, ydoc]);

  useEffect(() => { if (card && editor) setTimeout(tryInitContent, 100); }, [card, editor, tryInitContent]);

  useEffect(() => {
    if (!editor || !card) return;
    const handler = () => {
      setSaveStatus('saving');
      clearTimeout(saveTimer.current);
      saveTimer.current = setTimeout(async () => {
        try {
          await api.put(`/kanban/cards/${card.id}`, { description: editor.getHTML() });
          setSaveStatus('saved');
          clearTimeout(saveStatusTimer.current);
          saveStatusTimer.current = setTimeout(() => setSaveStatus(null), 2000);
        } catch {
          setSaveStatus('error');
        }
      }, 2000);
    };
    editor.on('update', handler);
    return () => {
      editor.off('update', handler);
      clearTimeout(saveTimer.current);
      clearTimeout(saveStatusTimer.current);
    };
  }, [editor, card]);

  const handleImageButton = () => fileInputRef.current?.click();
  const handleMdUpload = () => mdFileInputRef.current?.click();
  const handleMdFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !editor) return;
    const text = await file.text();
    const html = markdownToHtml(text);
    const view = editor.view;
    const parser = PMMDOMParser.fromSchema(view.state.schema);
    const dom = document.createElement('div');
    dom.innerHTML = html;
    const slice = parser.parseSlice(dom, { preserveWhitespace: false });
    const tr = view.state.tr.replaceSelection(slice);
    view.dispatch(tr.scrollIntoView());
    e.target.value = '';
  };
  const handleFileSelect = async (e) => {
    const files = e.target.files;
    if (!files?.length || !editor) return;
    for (const file of Array.from(files)) {
      if (!file.type.startsWith('image/')) continue;
      const url = await uploadImage(file);
      editor.chain().focus().setImage({ src: url }).run();
    }
    e.target.value = '';
  };

  const p = PRIORITY_MAP[card?.priority] || PRIORITY_MAP.medium;
  const PriorityIcon = p.icon;
  const isOverdue = card?.dueDate && card.status !== 'done' && dayjs(card.dueDate).isBefore(dayjs(), 'day');

  return (
    <div className="flex flex-col h-full bg-gray-950">
      {/* 헤더 */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-800 bg-gray-950 flex-shrink-0">
        <div className="flex items-center gap-3 min-w-0 flex-1">
          <button onClick={() => navigate(backPath)}
            className="p-1.5 text-gray-500 hover:text-white rounded-lg hover:bg-gray-800 flex-shrink-0">
            <ArrowLeft size={18} />
          </button>
          <h1 className="text-white text-lg font-bold truncate">{card?.title || '로딩 중...'}</h1>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <div className="flex -space-x-1.5">
            {peers.slice(0, 5).map((p, i) => (
              <div key={i}
                className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold border-2 ${p.isMe ? 'border-brand' : 'border-gray-950'}`}
                style={{ backgroundColor: p.color, color: 'white' }}
                title={p.isMe ? `${p.name} (나)` : p.name}>
                {p.name?.charAt(0)}
              </div>
            ))}
          </div>
          {saveStatus === 'saving' && <span className="text-gray-500 text-xs">저장 중...</span>}
          {saveStatus === 'saved' && <span className="text-green-500 text-xs">저장됨</span>}
          {saveStatus === 'error' && <span className="text-red-400 text-xs">저장 실패</span>}
          {!connected && <WifiOff size={13} className="text-gray-500" />}
          <button onClick={() => setEditModal(true)}
            className="p-1.5 text-gray-500 hover:text-white rounded-lg hover:bg-gray-800">
            <Pencil size={15} />
          </button>
          <button onClick={handleDelete} disabled={deleting}
            className="p-1.5 text-gray-500 hover:text-red-400 rounded-lg hover:bg-gray-800 disabled:opacity-40">
            <Trash2 size={15} />
          </button>
        </div>
      </div>

      {/* 카드 메타 정보 */}
      {card && (
        <div className="flex items-center gap-4 px-6 py-2.5 border-b border-gray-800 flex-shrink-0 flex-wrap">
          {/* 상태 */}
          <span className={`flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-medium ${STATUS_COLOR[card.status]}`}>
            <CheckSquare size={11} />
            {STATUS_LABEL[card.status]}
          </span>

          {/* 우선순위 */}
          <span className={`flex items-center gap-1 text-xs ${p.color}`}>
            <PriorityIcon size={11} />{p.label}
          </span>

          {/* 라벨 */}
          {card.label && (
            <span className="flex items-center gap-1 text-xs px-2 py-0.5 rounded-full"
              style={{ backgroundColor: card.label.color + '33', color: card.label.color }}>
              <Tag size={10} /> {card.label.name}
            </span>
          )}

          {/* 마감일 */}
          {card.dueDate && (
            <span className={`flex items-center gap-1 text-xs ${isOverdue ? 'text-red-400' : 'text-gray-500'}`}>
              <Calendar size={11} />
              {dayjs(card.dueDate).format('YYYY.MM.DD')}
              {isOverdue && ' (지남)'}
            </span>
          )}

          {/* 담당자 */}
          {(card.assigneeDetails || []).length > 0 && (
            <span className="flex items-center gap-1 text-xs text-gray-400">
              <Users size={11} />
              {card.assigneeDetails.map(a => a.name).join(', ')}
            </span>
          )}

          {/* 작성자/날짜 */}
          <span className="flex items-center gap-1 text-xs text-gray-600 ml-auto">
            <Clock size={10} />
            {card.creator?.name} · {dayjs(card.createdAt).format('MM.DD HH:mm')}
          </span>
        </div>
      )}

      {/* 에디터 */}
      <div className="flex flex-col flex-1 min-h-0">
        <EditorToolbar editor={editor} onImageUpload={handleImageButton} onMarkdownUpload={handleMdUpload} />
        <div className="flex-1 overflow-y-auto">
          <EditorContent editor={editor} />
        </div>
        <input ref={fileInputRef} type="file" accept="image/*" multiple className="hidden" onChange={handleFileSelect} />
        <input ref={mdFileInputRef} type="file" accept=".md,text/markdown" className="hidden" onChange={handleMdFile} />
        <FileAttachments entityType="kanban" entityId={card?.id} editor={editor} />
      </div>

      {/* 수정 모달 */}
      {editModal && card && (
        <EditCardModal
          card={card}
          labels={labels}
          admins={admins}
          onClose={() => setEditModal(false)}
          onSave={handleEditSave}
        />
      )}
    </div>
  );
}

function EditCardModal({ card, labels, admins, onClose, onSave }) {
  const [form, setForm] = useState({
    title: card.title || '',
    status: card.status || 'todo',
    priority: card.priority || 'medium',
    labelId: card.labelId || '',
    assignees: card.assignees || [],
    dueDate: card.dueDate ? card.dueDate.slice(0, 10) : '',
  });
  const [saving, setSaving] = useState(false);

  const toggle = (id) => setForm(p => ({
    ...p,
    assignees: p.assignees.includes(id) ? p.assignees.filter(a => a !== id) : [...p.assignees, id],
  }));

  const handleSubmit = async e => {
    e.preventDefault();
    if (!form.title.trim()) return;
    setSaving(true);
    await onSave({ ...form, labelId: form.labelId || null, dueDate: form.dueDate || null });
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={onClose}>
      <div className="bg-gray-900 border border-gray-700 rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b border-gray-800">
          <h2 className="text-sm font-semibold text-white">카드 수정</h2>
          <button onClick={onClose} className="p-1 text-gray-500 hover:text-white rounded-lg hover:bg-gray-800">
            <X size={16} />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-5 flex flex-col gap-4">
          <div>
            <label className="text-xs text-gray-400 mb-1 block">제목 *</label>
            <input
              className="w-full bg-gray-800 border border-gray-700 rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none focus:border-brand"
              value={form.title} onChange={e => setForm(p => ({ ...p, title: e.target.value }))} autoFocus required
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-gray-400 mb-1 block">상태</label>
              <select className="w-full bg-gray-800 border border-gray-700 rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none"
                value={form.status} onChange={e => setForm(p => ({ ...p, status: e.target.value }))}>
                {COLUMNS_STATUS.map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs text-gray-400 mb-1 block">우선순위</label>
              <select className="w-full bg-gray-800 border border-gray-700 rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none"
                value={form.priority} onChange={e => setForm(p => ({ ...p, priority: e.target.value }))}>
                <option value="low">낮음</option>
                <option value="medium">보통</option>
                <option value="high">높음</option>
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-gray-400 mb-1 block">라벨</label>
              <select className="w-full bg-gray-800 border border-gray-700 rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none"
                value={form.labelId} onChange={e => setForm(p => ({ ...p, labelId: e.target.value }))}>
                <option value="">없음</option>
                {labels.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs text-gray-400 mb-1 block">마감일</label>
              <input type="date" className="w-full bg-gray-800 border border-gray-700 rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none"
                value={form.dueDate} onChange={e => setForm(p => ({ ...p, dueDate: e.target.value }))} />
            </div>
          </div>
          {admins.length > 0 && (
            <div>
              <label className="text-xs text-gray-400 mb-2 block">담당자</label>
              <div className="flex flex-wrap gap-2">
                {admins.map(a => {
                  const selected = form.assignees.includes(a.id);
                  return (
                    <button key={a.id} type="button" onClick={() => toggle(a.id)}
                      className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-xl text-xs border transition-all ${
                        selected ? 'bg-brand/20 border-brand text-brand' : 'bg-gray-800 border-gray-700 text-gray-400 hover:border-gray-500'
                      }`}>
                      <Avatar name={a.name} />
                      <span>{a.name}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}
          <div className="flex gap-2 justify-end pt-1">
            <button type="button" onClick={onClose}
              className="px-4 py-2 text-xs text-gray-400 border border-gray-700 rounded-xl hover:bg-gray-800">
              취소
            </button>
            <button type="submit" disabled={saving}
              className="px-4 py-2 text-xs text-white bg-brand rounded-xl hover:bg-brand/80 disabled:opacity-50">
              {saving ? '저장 중...' : '수정'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
